// eslint-disable-next-line no-restricted-imports -- This is the custom wrapper that enforces tooltips
import { Box, IconButton as MuiIconButton, Tooltip } from '@mui/material';
import type {
  IconButtonProps as MuiIconButtonProps,
  TooltipProps,
} from '@mui/material';
import cx from 'classnames';
import type { LinkProps } from 'react-router';

import styles from './IconButton.module.scss';
import { Icon, IconProps } from '../Icon';

type IconButtonVariant =
  | 'text'
  | 'outlined'
  | 'squared'
  | 'contained'
  | 'rounded';

// Base props shared by all variants
interface BaseIconButtonProps {
  /** Tooltip text to display on hover/focus */
  tooltip: string;
  /** Icon element to render inside the button */
  iconName: string;
  /** Button variant - standard (default) or outlined */
  variant?: IconButtonVariant;
  /** Whether tooltip should be disabled */
  disableTooltip?: boolean;
  /** Additional props to pass to the Tooltip component */
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
  iconProps?: Omit<IconProps, 'name' | 'size'>;
}

// Button variant (default) - no href allowed
interface ButtonIconButtonProps
  extends BaseIconButtonProps, Omit<MuiIconButtonProps, 'title' | 'component'> {
  component?: 'button' | undefined;
  selected?: boolean; // use for ToggleButton-like behavior
  href?: never;
  target?: never;
  rel?: never;
  to?: never;
  state?: never;
}

// Anchor variant - href allowed and encouraged
interface AnchorIconButtonProps
  extends BaseIconButtonProps, Omit<MuiIconButtonProps, 'title' | 'component'> {
  component: 'a';
  selected?: never;
  href?: string;
  target?: string;
  rel?: string;
  download?: boolean | string;
  to?: never;
  state?: never;
}

// Link variant - React Router Link props allowed
interface LinkIconButtonProps
  extends
    BaseIconButtonProps,
    Omit<MuiIconButtonProps, 'title' | 'component'>,
    Pick<
      LinkProps,
      'to' | 'state' | 'replace' | 'preventScrollReset' | 'relative'
    > {
  component: typeof import('react-router').Link;
  selected?: never;
  href?: never;
  target?: never;
  rel?: never;
  download?: never;
}

// Union type for the component
export type IconButtonProps =
  | ButtonIconButtonProps
  | AnchorIconButtonProps
  | LinkIconButtonProps;

export const IconButton = ({
  tooltip,
  iconName,
  variant = 'text',
  disableTooltip = false,
  tooltipProps,
  selected,
  iconProps,
  ...props
}: IconButtonProps) => {
  const iconButton = (
    <MuiIconButton
      aria-label={tooltip}
      {...props}
      className={cx(styles.IconButton, props.className, {
        [styles.IconButtonSelected]: selected,
        [styles.IconButtonContained]: variant === 'contained',
        [styles.IconButtonText]: variant === 'text',
        [styles.IconButtonOutlined]: variant === 'outlined',
        [styles.IconButtonSquared]: variant === 'squared',
        [styles.IconButtonRounded]: variant === 'rounded',
      })}
    >
      <Icon name={iconName} size={props.size} {...iconProps} />
    </MuiIconButton>
  );

  if (disableTooltip) {
    return iconButton;
  }

  // For disabled buttons, wrap in span to enable tooltip
  const tooltipChild = props.disabled ? (
    <Box component="span" sx={{ display: 'inline-block' }}>
      {iconButton}
    </Box>
  ) : (
    iconButton
  );

  return (
    <Tooltip title={tooltip} arrow {...tooltipProps}>
      {tooltipChild}
    </Tooltip>
  );
};

IconButton.displayName = 'IconButton';
